import { AxiosError } from "axios";
import { ErrorResponse } from "../../errors/errorHandler";

/**
 * แปลง error ของขั้นตอน "ตรวจสอบไฟล์" เป็นข้อความที่แสดงในหน้าจอ
 *
 * ขั้นนี้จงใจไม่ใช้ global error toast: เหตุผลที่ไฟล์ไม่ผ่านเกือบทั้งหมดเป็น
 * เรื่องของ "ไฟล์" ไม่ใช่ "ระบบ" ผู้ใช้ต้องเห็นมันคู่กับปุ่มดาวน์โหลดแบบฟอร์ม
 * ตรงจุดที่เพิ่งเลือกไฟล์ ไม่ใช่เป็น toast ที่หายไปใน 5 วินาที
 */

/** 422 = backend อ่านไฟล์แล้วแต่ใช้ไม่ได้ และส่งเหตุผลภาษาไทยมาให้เสมอ */
const FILE_LEVEL_STATUS = 422;

export const PATHWAY_PREVIEW_ERROR_BY_STATUS: Record<number, string> = {
  400: "ไม่พบไฟล์ หรือไฟล์มีขนาดใหญ่เกินไป (สูงสุด 30MB)",
  403: "คุณไม่มีสิทธิ์อัปโหลดแผนวิ่งของสาขานี้",
  415: "ไฟล์ไม่รองรับ กรุณาอัปโหลดไฟล์ .xlsx หรือ .xls เท่านั้น",
};

export interface PathwayUploadFailure {
  message: string;
  /** จริงเมื่อสาเหตุน่าจะเป็น "ไฟล์ผิดแบบฟอร์ม" — แสดงปุ่มดาวน์โหลดแบบฟอร์มคู่ไปด้วย */
  showTemplateHint: boolean;
}

const serverMessageOf = (error: AxiosError<ErrorResponse>): string | null => {
  const data = error.response?.data;
  if (!data || typeof data !== "object") return null;

  const message = data.message ?? data.error ?? data.detail;
  return typeof message === "string" && message.trim() !== "" ? message : null;
};

export const resolvePreviewFailure = (error: unknown): PathwayUploadFailure => {
  const axiosError = error as AxiosError<ErrorResponse>;
  const status = axiosError?.response?.status;

  if (status === FILE_LEVEL_STATUS) {
    return {
      // backend เป็นตัวเดียวที่รู้ว่าติดกฎข้อไหน (ไม่พบชีต ผ.1 / ไม่พบหัวตารางวัน /
      // ชีตมากกว่าจำนวนสายของสาขา) จึงใช้ข้อความของ backend ตรง ๆ
      message: serverMessageOf(axiosError) ?? "ไฟล์นี้อ่านเป็นแผนวิ่งไม่ได้",
      showTemplateHint: true,
    };
  }

  if (status !== undefined && PATHWAY_PREVIEW_ERROR_BY_STATUS[status]) {
    return {
      message: PATHWAY_PREVIEW_ERROR_BY_STATUS[status],
      showTemplateHint: status === 415,
    };
  }

  if (axiosError?.response) {
    return {
      message: serverMessageOf(axiosError) ?? `เกิดข้อผิดพลาด (Error ${status})`,
      showTemplateHint: false,
    };
  }

  return {
    message: "ติดต่อเซิร์ฟเวอร์ไม่ได้ กรุณาตรวจสอบอินเทอร์เน็ตแล้วลองใหม่",
    showTemplateHint: false,
  };
};
